import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Menu,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Repeat as RepeatIcon } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import api from "../../services/api";
import AssigneeDropdown from "../../utils/AssigneeDropdown";
import StatusChip from "../../utils/StatusChip";
import { slotOptions, WRITABLE_SLOTS, SLOT_LABELS } from "../../utils/sprintSlots";
import {
  STATUS_OPTIONS,
  CATEGORY_OPTIONS,
  CLASS_OPTIONS,
  isMeetingTask as isMeeting,
} from "../../utils/constants";
import { formatFloatToTimeHM } from "../../utils/timeFormatter";
import { FieldLabel, FormSection, FIELD_HEIGHT } from "../common/FormSection";

// --- Repeat control ----------------------------------------------------------

const FREQUENCIES = [
  { value: "daily", label: "Daily", unit: "day" },
  { value: "weekly", label: "Weekly", unit: "week" },
  { value: "monthly", label: "Monthly", unit: "month" },
  { value: "quarterly", label: "Quarterly", unit: "quarter" },
  { value: "annual", label: "Annually", unit: "year" },
];
// Backend weekday numbering: 0 = Monday … 6 = Sunday.
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/** "Every Monday", "Every 2 months on the 1st", "Every year on 1 April", … */
export const describeRecurrence = (rule) => {
  if (!rule || !rule.is_active) return "Does not repeat";
  const freq = FREQUENCIES.find((f) => f.value === rule.frequency);
  const n = rule.interval || 1;
  const every = n === 1 ? `Every ${freq?.unit}` : `Every ${n} ${freq?.unit}s`;
  switch (rule.frequency) {
    case "daily":
      return n === 1 ? "Daily" : every;
    case "weekly":
      return n === 1
        ? `Every ${WEEKDAYS[rule.weekday]}`
        : `${every} on ${WEEKDAYS[rule.weekday]}`;
    case "monthly":
    case "quarterly":
      return `${every} on the ${ordinal(rule.day_of_month)}`;
    case "annual":
      return `${every} on ${rule.day_of_month} ${MONTHS[rule.month - 1]}`;
    default:
      return "Repeats";
  }
};

// Anchor defaults taken from the task's do_date (or today) when a frequency is picked.
const defaultsFor = (frequency, doDate) => {
  const d = doDate ? dayjs(doDate) : dayjs();
  return {
    frequency,
    interval: 1,
    weekday: (d.day() + 6) % 7,
    day_of_month: d.date(),
    month: d.month() + 1,
  };
};

/**
 * The Repeat control in the task form's Schedule row. Owns its own persistence
 * (`tasks/<id>/recurrence/`) since the rule is a separate resource from the
 * task's fields. Disabled until the task exists.
 *
 * Once a recurring task's occurrence is completed, the backend generates the
 * next one with the next do_date; nothing is created while one is still open.
 *
 * @param {Object} props
 * @param {number|string|null} props.taskId
 * @param {number} props.workspaceId
 * @param {Object|null} props.value The task's `recurrence` summary.
 * @param {string|null} [props.doDate] Seeds the anchor day for a new rule.
 * @param {(rule: Object|null) => void} [props.onSaved]
 * @param {boolean} [props.disabled]
 */
export const RecurrenceControl = ({ taskId, workspaceId, value, doDate, onSaved, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const active = Boolean(value?.is_active);

  const open = (e) => {
    setError("");
    setDraft(active ? { ...value } : defaultsFor("weekly", doDate));
    setAnchorEl(e.currentTarget);
  };

  const set = (field, v) => setDraft((prev) => ({ ...prev, [field]: v }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await api.put(`tasks/${taskId}/recurrence/`, {
        workspace: workspaceId,
        frequency: draft.frequency,
        interval: Number(draft.interval) || 1,
        weekday: draft.weekday,
        day_of_month: draft.day_of_month,
        month: draft.month,
      });
      onSaved?.(res.data);
      setAnchorEl(null);
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data?.detail ||
          (data && typeof data === "object" ? Object.values(data).flat().join(" ") : "") ||
          "The repeat rule could not be saved."
      );
    } finally {
      setSaving(false);
    }
  };

  const stop = async () => {
    setSaving(true);
    try {
      await api.delete(`tasks/${taskId}/recurrence/`, {
        params: { workspace_id: workspaceId },
      });
      onSaved?.(value ? { ...value, is_active: false } : null);
      setAnchorEl(null);
    } catch (err) {
      setError("Could not stop repeating.");
    } finally {
      setSaving(false);
    }
  };

  const freq = FREQUENCIES.find((f) => f.value === draft?.frequency);

  return (
    <>
      <FieldLabel>Repeat</FieldLabel>
      <Button
        variant="outlined"
        color={active ? "primary" : "inherit"}
        size="small"
        startIcon={<RepeatIcon fontSize="small" />}
        disabled={disabled || !taskId}
        onClick={open}
        sx={{
          height: FIELD_HEIGHT,
          textTransform: "none",
          whiteSpace: "nowrap",
          borderColor: active ? undefined : "divider",
          color: active ? undefined : "text.secondary",
        }}
      >
        {describeRecurrence(value)}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {draft && (
          <Box sx={{ p: 2, width: 280, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <FieldLabel>Frequency</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select
                    value={draft.frequency}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...defaultsFor(e.target.value, doDate),
                        interval: prev.interval,
                        frequency: e.target.value,
                      }))
                    }
                  >
                    {FREQUENCIES.map((f) => (
                      <MenuItem key={f.value} value={f.value}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ width: 90 }}>
                <FieldLabel>Every</FieldLabel>
                <TextField
                  type="number"
                  size="small"
                  value={draft.interval ?? 1}
                  onChange={(e) => set("interval", e.target.value)}
                  slotProps={{ htmlInput: { min: 1 } }}
                  helperText={`${freq?.unit}(s)`}
                />
              </Box>
            </Box>

            {draft.frequency === "weekly" && (
              <Box>
                <FieldLabel>On</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select value={draft.weekday ?? 0} onChange={(e) => set("weekday", e.target.value)}>
                    {WEEKDAYS.map((day, i) => (
                      <MenuItem key={day} value={i}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {["monthly", "quarterly", "annual"].includes(draft.frequency) && (
              <Box sx={{ display: "flex", gap: 1 }}>
                {draft.frequency === "annual" && (
                  <Box sx={{ flex: 1 }}>
                    <FieldLabel>Month</FieldLabel>
                    <FormControl fullWidth size="small">
                      <Select value={draft.month ?? 1} onChange={(e) => set("month", e.target.value)}>
                        {MONTHS.map((m, i) => (
                          <MenuItem key={m} value={i + 1}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <Box sx={{ width: draft.frequency === "annual" ? 90 : "100%" }}>
                  <FieldLabel>Day of month</FieldLabel>
                  <FormControl fullWidth size="small">
                    <Select
                      value={draft.day_of_month ?? 1}
                      onChange={(e) => set("day_of_month", e.target.value)}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <MenuItem key={d} value={d}>
                          {ordinal(d)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              {describeRecurrence({ ...draft, is_active: true })}. The next task is
              created when this one is completed.
            </Typography>
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              {active ? (
                <Button size="small" color="error" onClick={stop} disabled={saving}>
                  Stop repeating
                </Button>
              ) : (
                <span />
              )}
              <Button size="small" variant="contained" onClick={save} disabled={saving}>
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
};

/**
 * The single task form used by every task surface (create dialog, detail page,
 * detail drawer). Keeping one component means the fields always appear in the
 * same order and the same place, so the layout is muscle-memory stable:
 *
 *   Task Name → [Action Items] → Notes → People → Schedule → Categorization
 *
 * The name reads as the surface's title. The Action Items step is a slot: the
 * detail page and drawer fill it, so the part of the task people actually work
 * in sits right under the title. The create dialog leaves it empty and renders
 * its (still-disabled) table at the bottom instead, since while creating a task
 * the classification fields are the work.
 *
 * It is purely presentational: the caller owns the values, the option lists and
 * the persistence strategy (create-on-valid vs. debounced PATCH).
 *
 * @param {Object} props
 * @param {Object} props.values Current field values, keyed by task field name.
 *   Also accepts the read-only `epic`, `project`, `sprint_window` and
 *   `time_taken_total` helpers.
 * @param {Object} props.options `{ epics, projects, tracks, allTracks,
 *   departments, divisions }`. `tracks` is the Track picker's project-scoped
 *   list; `allTracks` is every workspace track, used to go from a division to
 *   its track (and project).
 * @param {Array} [props.actionItems] The task's action items. Only their `type`
 *   is read, to decide whether this is a meeting task (which reveals the
 *   Start/End Time fields).
 * @param {React.ReactNode} [props.actionItemsSlot] Rendered between the name and
 *   the notes. Both surfaces pass their action-item table here so the block
 *   cannot drift out of position on one of them.
 * @param {React.ReactNode} [props.recurrenceSlot] Rendered in the Schedule
 *   row after Do Date (the Repeat control).
 * @param {React.ReactNode} [props.headerActions] Rendered at the right of the
 *   name row (the detail page's copy-link / show-as-drawer buttons).
 * @param {React.ReactNode} [props.nameActions] Rendered on its own line directly
 *   under the name (the create dialog's Template button), so actions that act on
 *   the whole task sit with the title rather than adrift further down the form.
 * @param {Object} [props.errors] `{ [field]: boolean }` — fields to flag as invalid.
 * @param {Object} [props.helperText] `{ [field]: string }` — message under a flagged field.
 * @param {(field: string, value: any) => void} props.onChange
 * @param {boolean} [props.disabled] Renders every control read-only.
 */
const TaskFormFields = ({
  values,
  options,
  actionItems = [],
  actionItemsSlot = null,
  recurrenceSlot = null,
  headerActions = null,
  nameActions = null,
  errors = {},
  helperText = {},
  onChange,
  disabled = false,
}) => {
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);

  const {
    epics = [],
    projects = [],
    tracks = [],
    allTracks = [],
    departments = [],
    divisions = [],
  } = options || {};

  // A meeting task is time-blocked on the Calendar View; expose start/end time.
  // Type now lives on the action items, so it is they that decide this.
  const isMeetingTask = isMeeting(values.category, actionItems);
  // Foreign keys arrive as numbers from some endpoints and strings from others
  // (and the create dialog holds project/track as strings so its own option
  // values match). Comparing raw values meant a Select whose `value` was a
  // number never matched its string MenuItem and rendered blank, which is what
  // made Department/Division look like they weren't filtering. Everything is
  // funnelled through `id()` so the comparison is on one type.
  const id = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

  // The division picker is scoped to the chosen department. With no department
  // yet every division is offered, so the form can be started from a division
  // (picking one fills in its department).
  const filteredDivisions = values.department
    ? divisions.filter((d) => id(d.department) === id(values.department))
    : divisions;

  // Changing department drops a division that no longer belongs to it.
  const handleDepartmentChange = (department) => {
    onChange("department", department === "" ? null : Number(department));
    const stillValid = divisions.some(
      (d) => id(d.id) === id(values.division) && id(d.department) === id(department)
    );
    if (!stillValid) onChange("division", null);
  };

  const findTrack = (trackId) =>
    allTracks.find((t) => id(t.id) === id(trackId)) ||
    tracks.find((t) => id(t.id) === id(trackId));

  // Tracks whose home division is the chosen one. Narrows the Track picker, and
  // is how picking a division finds its project and track.
  const divisionTracks = values.division
    ? allTracks.filter((t) => id(t.division) === id(values.division))
    : [];
  const trackOptions =
    divisionTracks.length > 0
      ? tracks.filter((t) => divisionTracks.some((d) => id(d.id) === id(t.id)))
      : tracks;

  // A track that belongs to a different division than the one chosen.
  const selectedTrack = findTrack(values.track);
  const trackMismatch = Boolean(
    selectedTrack?.division != null &&
      values.division != null &&
      id(selectedTrack.division) !== id(values.division)
  );
  const mismatchText = trackMismatch
    ? `Track "${selectedTrack.name}" belongs to ${
        selectedTrack.division_name || "another division"
      }`
    : "";

  // Picking a division selects its track (and that track's project). With
  // several tracks in the division the track is cleared so the user picks from
  // the narrowed list; a current track already in the division is kept.
  const handleDivisionChange = (value) => {
    const divisionId = value === "" ? null : Number(value);
    onChange("division", divisionId);
    if (divisionId == null) return;

    const division = divisions.find((d) => id(d.id) === id(divisionId));
    if (division?.department != null && id(division.department) !== id(values.department)) {
      onChange("department", division.department);
    }

    const candidates = allTracks.filter((t) => id(t.division) === id(divisionId));
    if (candidates.length === 0) return;
    if (candidates.some((t) => id(t.id) === id(values.track))) return;

    const projectIds = [...new Set(candidates.map((t) => id(t.project)))];
    if (projectIds.length === 1 && projectIds[0] !== id(values.project)) {
      // Changing the project clears the track in the callers, so it goes first.
      onChange("project", projectIds[0]);
    }
    onChange("track", candidates.length === 1 ? id(candidates[0].id) : "");
  };

  // Picking a track fills in the department/division it is linked to. A track
  // with neither leaves the task's current choice alone.
  const handleTrackChange = (trackId) => {
    onChange("track", trackId);
    const track = findTrack(trackId);
    if (!track || (track.department == null && track.division == null)) return;
    if (id(track.department) !== id(values.department)) {
      onChange("department", track.department ?? null);
    }
    if (id(track.division) !== id(values.division)) {
      onChange("division", track.division ?? null);
    }
  };

  const errorSx = (field) => ({
    backgroundColor: errors[field] ? "rgba(255, 0, 0, 0.08)" : "transparent",
  });

  const FieldError = ({ field }) =>
    errors[field] && helperText[field] ? (
      <Typography variant="caption" color="error">
        {helperText[field]}
      </Typography>
    ) : null;

  return (
    <Grid container spacing={1.5}>
      {/* 1. Task Name — the surface's title, edited in place. */}
      <Grid size={12}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <TextField
            value={values.name || ""}
            fullWidth
            variant="standard"
            disabled={disabled}
            placeholder="Enter a task name"
            onChange={(e) => onChange("name", e.target.value)}
            required
            error={Boolean(errors.name)}
            helperText={errors.name ? helperText.name || "" : ""}
            slotProps={{
              input: { disableUnderline: !errors.name },
              htmlInput: {
                "aria-label": "Task name",
                style: { fontSize: "1.5rem", fontWeight: 500 },
              },
            }}
          />
          {headerActions}
        </Box>
        {nameActions && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}
          >
            {nameActions}
          </Box>
        )}
      </Grid>

      {/* 2. Action Items */}
      {actionItemsSlot && <Grid size={12}>{actionItemsSlot}</Grid>}

      {/* 3. Notes */}
      <Grid size={12}>
        <FieldLabel>Notes</FieldLabel>
        <TextField
          value={values.notes || ""}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          disabled={disabled}
          placeholder="Add details, context, or links"
          onChange={(e) => onChange("notes", e.target.value)}
        />
      </Grid>

      {/* 4. People + 5. Schedule */}
      <Grid size={12}>
        {/* Both panels are sized by their own fields rather than by a column
            split, so neither is padded out with empty space: People sits at
            the left edge, Schedule at the right. They wrap onto separate
            lines when the surface is too narrow to hold both. */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "stretch",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ flex: "0 1 auto" }}>
            <FormSection title="People" sx={{ height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ width: 150 }}>
                  <FieldLabel required>Assignee</FieldLabel>
                  <Box
                    sx={{
                      minHeight: FIELD_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                      borderRadius: 1,
                      ...errorSx("assignee"),
                    }}
                  >
                    <AssigneeDropdown
                      currentSelected={values.assignee}
                      onChange={(id) => onChange("assignee", id)}
                    />
                  </Box>
                  <FieldError field="assignee" />
                </Box>
                <Box sx={{ width: 150 }}>
                  <FieldLabel>Reviewer</FieldLabel>
                  <Box
                    sx={{
                      minHeight: FIELD_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <AssigneeDropdown
                      currentSelected={values.reviewer}
                      onChange={(id) => onChange("reviewer", id)}
                    />
                  </Box>
                </Box>
              </Box>
            </FormSection>
          </Box>
          <Box sx={{ flex: "0 1 auto" }}>
            <FormSection title="Schedule" sx={{ height: "100%" }}>
              {/* One row: Line Up / Do Date / Time Taken / Sprint / Status,
                  plus the meeting-only times. Every field is sized to its own
                  content — none of them stretch, so a date or a status chip
                  never balloons to fill the panel. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Box sx={{ flex: "0 0 66px" }}>
                  <FieldLabel required>Line Up</FieldLabel>
                  <TextField
                    type="number"
                    size="small"
                    value={values.line_up ?? ""}
                    fullWidth
                    disabled={disabled}
                    onChange={(e) => onChange("line_up", e.target.value)}
                    required
                  />
                </Box>
                <Box sx={{ flex: "0 0 150px" }}>
                  <FieldLabel>Do Date</FieldLabel>
                  <TextField
                    type="date"
                    size="small"
                    value={
                      values.do_date
                        ? dayjs(values.do_date).format("YYYY-MM-DD")
                        : ""
                    }
                    fullWidth
                    disabled={disabled}
                    onChange={(e) => onChange("do_date", e.target.value)}
                  />
                </Box>
                {recurrenceSlot && (
                  <Box sx={{ flex: "0 0 auto" }}>{recurrenceSlot}</Box>
                )}
                <Box sx={{ flex: "0 0 78px" }}>
                  <FieldLabel>Time Taken</FieldLabel>
                  <TextField
                    size="small"
                    value={formatFloatToTimeHM(values.time_taken_total || 0)}
                    fullWidth
                    disabled
                  />
                </Box>
                {isMeetingTask && (
                  <>
                    <Box sx={{ flex: "0 0 88px" }}>
                      <FieldLabel>Start Time</FieldLabel>
                      <TextField
                        type="time"
                        size="small"
                        value={
                          values.start_time
                            ? String(values.start_time).slice(0, 5)
                            : ""
                        }
                        fullWidth
                        disabled={disabled}
                        onChange={(e) =>
                          onChange("start_time", e.target.value || null)
                        }
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 88px" }}>
                      <FieldLabel>End Time</FieldLabel>
                      <TextField
                        type="time"
                        size="small"
                        value={
                          values.end_time
                            ? String(values.end_time).slice(0, 5)
                            : ""
                        }
                        fullWidth
                        disabled={disabled}
                        onChange={(e) =>
                          onChange("end_time", e.target.value || null)
                        }
                      />
                    </Box>
                  </>
                )}
                {/* Sprint slots the task into the schedule, so it lives here
                    rather than with the taxonomy fields. Status sits after it,
                    closing the row. */}
                <Box sx={{ flex: "0 0 140px" }}>
                  <FieldLabel required>Sprint</FieldLabel>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={disabled || !values.track}
                    error={Boolean(errors.sprint_slot)}
                  >
                    <Select
                      value={
                        WRITABLE_SLOTS.includes(values.sprint_slot)
                          ? values.sprint_slot
                          : "backlog"
                      }
                      onChange={(e) => onChange("sprint_slot", e.target.value)}
                    >
                      {/* Backlog is derived (a task ages into it); shown when
                          active but never directly selectable. */}
                      {!WRITABLE_SLOTS.includes(values.sprint_slot) && (
                        <MenuItem value="backlog" disabled>
                          {SLOT_LABELS.backlog}
                        </MenuItem>
                      )}
                      {slotOptions(values.sprint_window).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: "0 0 140px" }}>
                  <FieldLabel>Status</FieldLabel>
                  <StatusChip
                    status={values.status}
                    label={
                      STATUS_OPTIONS.includes(values.status)
                        ? values.status
                        : "None"
                    }
                    onClick={(e) =>
                      disabled ? null : setStatusAnchorEl(e.currentTarget)
                    }
                    sx={{
                      width: "100%",
                      justifyContent: "flex-start",
                      height: FIELD_HEIGHT,
                    }}
                  />
                  <Menu
                    anchorEl={statusAnchorEl}
                    open={Boolean(statusAnchorEl)}
                    onClose={() => setStatusAnchorEl(null)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => {
                          onChange("status", option);
                          setStatusAnchorEl(null);
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
            </FormSection>
          </Box>
        </Box>
      </Grid>

      {/* 6. Categorization — Epic / Project / Track on one line, then
          Department / Division / Category / Class on the next. */}
      <Grid size={12}>
        <FormSection title="Categorization">
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Epic</FieldLabel>
              <FormControl fullWidth size="small" disabled={disabled}>
                <Select
                  displayEmpty
                  value={id(values.epic)}
                  onChange={(e) => onChange("epic", e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {epics.map((epic) => (
                    <MenuItem key={epic.id} value={id(epic.id)}>
                      {epic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel required>Project</FieldLabel>
              <FormControl
                fullWidth
                size="small"
                disabled={disabled}
                error={Boolean(errors.project)}
              >
                <Select
                  displayEmpty
                  value={id(values.project)}
                  onChange={(e) => onChange("project", e.target.value)}
                  sx={errorSx("project")}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={id(project.id)}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
                <FieldError field="project" />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel required>Track</FieldLabel>
              <FormControl
                fullWidth
                size="small"
                disabled={disabled || !values.project}
                error={Boolean(errors.track) || trackMismatch}
              >
                <Select
                  displayEmpty
                  value={id(values.track)}
                  onChange={(e) => handleTrackChange(e.target.value)}
                  sx={errorSx("track")}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {/* Keep a mismatched current track listed so it still renders. */}
                  {selectedTrack &&
                    !trackOptions.some((t) => id(t.id) === id(selectedTrack.id)) && (
                      <MenuItem value={id(selectedTrack.id)}>
                        {selectedTrack.name}
                      </MenuItem>
                    )}
                  {trackOptions.map((track) => (
                    <MenuItem key={track.id} value={id(track.id)}>
                      {track.name}
                    </MenuItem>
                  ))}
                </Select>
                <FieldError field="track" />
                {trackMismatch && (
                  <Typography variant="caption" color="error">
                    {mismatchText}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <FieldLabel required>Department</FieldLabel>
              <FormControl
                fullWidth
                size="small"
                disabled={disabled}
                error={Boolean(errors.department)}
              >
                <Select
                  displayEmpty
                  value={id(values.department)}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  sx={errorSx("department")}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {departments.map((department) => (
                    <MenuItem key={department.id} value={id(department.id)}>
                      {department.name}
                    </MenuItem>
                  ))}
                </Select>
                <FieldError field="department" />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <FieldLabel required>Division</FieldLabel>
              <FormControl
                fullWidth
                size="small"
                disabled={disabled}
                error={Boolean(errors.division) || trackMismatch}
              >
                <Select
                  displayEmpty
                  value={id(values.division)}
                  onChange={(e) => handleDivisionChange(e.target.value)}
                  sx={errorSx("division")}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {filteredDivisions.map((division) => (
                    <MenuItem key={division.id} value={id(division.id)}>
                      {division.name}
                    </MenuItem>
                  ))}
                </Select>
                <FieldError field="division" />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <FieldLabel required>Category</FieldLabel>
              <FormControl
                fullWidth
                size="small"
                disabled={disabled}
                error={Boolean(errors.category)}
              >
                <Select
                  displayEmpty
                  value={values.category || ""}
                  onChange={(e) => onChange("category", e.target.value)}
                  sx={errorSx("category")}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {CATEGORY_OPTIONS.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                <FieldError field="category" />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <FieldLabel required>Class</FieldLabel>
              <FormControl fullWidth size="small" disabled={disabled}>
                <Select
                  displayEmpty
                  value={values.task_class || ""}
                  onChange={(e) => onChange("task_class", e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {CLASS_OPTIONS.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      {cls}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </FormSection>
      </Grid>
    </Grid>
  );
};

export default TaskFormFields;
